// Public app API client that talks to pro-admin serverless APIs
// Maps MongoDB-backed property shape to the public site Property interface

export interface ApiProperty {
  id: string
  title: string
  description?: string
  price?: number
  price_per_night?: number
  rent_price_per_day?: number
  full_price?: number
  // Prefer backend-provided display price/label when available
  display_price?: number
  display_price_label?: 'night' | 'day' | 'full'
  land_dimensions?: string
  section?: 'property' | 'car' | 'boat' | 'apartment' | 'beach house' | 'furniture' | 'building and selling' | 'land'
  location: string
  whatsapp_number?: string
  latitude?: number
  longitude?: number
  bedrooms?: number
  bathrooms?: number
  area?: number
  property_type?: string
  status?: 'available' | 'booked' | 'maintenance' | 'sold_out'
  images?: string[]
  image_url?: string
  video_url?: string
  amenities?: string[]
  house_rules?: string[]
  host_name?: string
  host_image?: string
  host_bio?: string
  host_response_rate?: number
  host_response_time?: string
  average_rating?: number
  rating?: number
  total_reviews?: number
  created_at?: string
  updated_at?: string
  // Add the missing properties:
  check_in_time?: string
  check_out_time?: string
  cancellation_policy?: string
  instant_book?: boolean
  max_guests?: number
  area_sqft?: number
  host_avatar?: string
}

export interface PublicProperty {
  id: string
  title: string
  description?: string
  category?: 'Apartment' | 'Boat' | 'Building & Selling' | 'Beach House' | 'Furniture' | 'Car' | 'Land'
  price_per_night: number
  rent_price_per_day?: number
  full_price?: number
  land_dimensions?: string
  display_price?: number
  display_price_label?: 'night' | 'day' | 'full'
  location: string
  image_url: string
  video_url?: string
  rating: number
  total_reviews: number
  created_at: string
  updated_at: string
  status?: 'available' | 'sold_out' | 'booked'
  section?: 'property' | 'car' | 'boat' | 'apartment' | 'beach house' | 'furniture' | 'building and selling' | 'land'
  whatsapp_number?: string
  bedrooms?: number
  bathrooms?: number
  max_guests?: number
  area_sqft?: number
  amenities?: string[]
  house_rules?: string[]
  check_in_time?: string
  check_out_time?: string
  cancellation_policy?: string
  host_name?: string
  host_avatar?: string
  host_response_rate?: number
  host_response_time?: string
  instant_book?: boolean
  images?: string[]
  latitude?: number
  longitude?: number
}

function mapCategoryFromString(input?: string): PublicProperty['category'] | undefined {
  if (!input) return undefined
  const pt = input.toLowerCase()
  if (pt.includes('car')) return 'Car'
  if (pt.includes('apartment')) return 'Apartment'
  if (pt.includes('boat')) return 'Boat'
  if (pt.includes('beach')) return 'Beach House'
  if (pt.includes('furniture')) return 'Furniture'
  if (pt.includes('building') || pt.includes('sell')) return 'Building & Selling'
  if (pt.includes('land')) return 'Land'
  return undefined
}

function deriveCategory(section?: string, property_type?: string): PublicProperty['category'] | undefined {
  // Prefer explicit section when provided, otherwise fall back to property_type
  return mapCategoryFromString(section) || mapCategoryFromString(property_type)
}

function toPublicProperty(p: ApiProperty): PublicProperty {
  const category = deriveCategory(p.section, p.property_type)
  // Encode asset URLs to handle spaces and special characters safely
  const encode = (url?: string) => (typeof url === 'string' ? encodeURI(url) : '')
  const encodeOpt = (url?: string) => (typeof url === 'string' ? encodeURI(url) : undefined)
  const encodedImages = Array.isArray(p.images) ? p.images.map((u) => encodeURI(u)) : undefined

  // Determine display pricing based on category
  let displayPrice: number | undefined
  let displayLabel: PublicProperty['display_price_label'] = 'night'
  if (typeof p.display_price === 'number' && p.display_price_label) {
    // Prefer backend-provided values
    displayPrice = p.display_price
    displayLabel = p.display_price_label
  } else if (category === 'Car' || category === 'Boat') {
    displayPrice = p.rent_price_per_day ?? p.full_price ?? p.price ?? 0
    displayLabel = p.rent_price_per_day ? 'day' : 'full'
  } else if (category === 'Land' || category === 'Building & Selling' || category === 'Furniture') {
    displayPrice = p.full_price ?? p.price ?? 0
    displayLabel = 'full'
  } else {
    displayPrice = p.price_per_night ?? p.price ?? 0
    displayLabel = 'night'
  }

  return {
    id: p.id,
    title: p.title,
    description: p.description,
    category,
    price_per_night: typeof p.price_per_night === 'number' ? p.price_per_night : (p.price || 0),
    rent_price_per_day: p.rent_price_per_day,
    full_price: p.full_price,
    land_dimensions: p.land_dimensions,
    display_price: displayPrice,
    display_price_label: displayLabel,
    location: p.location,
    image_url: encode(p.image_url || (p.images && p.images[0]) || ''),
    video_url: encodeOpt(p.video_url),
    rating: typeof p.rating === 'number' ? p.rating : (p.average_rating || 0),
    total_reviews: p.total_reviews || 0,
    created_at: p.created_at || new Date().toISOString(),
    updated_at: p.updated_at || new Date().toISOString(),
    status: (p.status as PublicProperty['status']) || 'available',
    section: p.section,
    whatsapp_number: p.whatsapp_number,
    bedrooms: p.bedrooms,
    bathrooms: p.bathrooms,
    max_guests: p.max_guests,
    area_sqft: p.area_sqft || p.area,
    amenities: p.amenities,
    house_rules: p.house_rules,
    check_in_time: p.check_in_time,
    check_out_time: p.check_out_time,
    cancellation_policy: p.cancellation_policy,
    host_name: p.host_name,
    host_avatar: p.host_avatar || p.host_image,
    host_response_rate: p.host_response_rate,
    host_response_time: p.host_response_time,
    instant_book: p.instant_book,
    images: encodedImages,
    latitude: p.latitude,
    longitude: p.longitude,
  }
}

function getApiBase() {
  const raw = (import.meta as any)?.env?.VITE_PUBLIC_API_BASE_URL || (import.meta as any)?.env?.VITE_API_BASE_URL || ''
  if (!raw) return ''
  return String(raw).replace(/\/+$/, '')
}

function buildUrl(path: string) {
  const base = getApiBase()
  return `${base}${path}`
}

export async function fetchProperties(): Promise<PublicProperty[]> {
  const url = buildUrl(`/api/properties`)
  try {
    const res = await fetch(url)
    // If server returns non-OK, degrade silently to empty list
    if (!res.ok) return []
    // Try to parse JSON regardless of header; fall back to []
    const json = await res
      .json()
      .catch(async () => {
        // Attempt to read text for debugging locally, but do not spam logs
        await res.text().catch(() => '')
        return null
      })
    if (!json || typeof json !== 'object') return []
    const list: ApiProperty[] = (json as any)?.data || []
    if (!Array.isArray(list)) return []
    return list.map(toPublicProperty)
  } catch (err) {
    console.error('Failed to fetch properties:', err)
    return []
  }
}

export async function fetchPropertyById(id: string): Promise<PublicProperty | null> {
  const url = buildUrl(`/api/properties/${id}`)
  try {
    const res = await fetch(url)
    if (!res.ok) return null
    const json = await res
      .json()
      .catch(async () => {
        await res.text().catch(() => '')
        return null
      })
    if (!json || typeof json !== 'object') return null
    const item: ApiProperty | undefined = (json as any)?.data
    return item ? toPublicProperty(item) : null
  } catch (err) {
    console.error('Failed to fetch property by id:', err)
    return null
  }
}